import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { apiClient } from '../lib/api';
import {
  Restaurant,
  LunchSummary,
  LunchOrder,
  User,
  MenuItem,
  ApiError,
  Country,
} from '../types';
import {
  ChefHat,
  Plus,
  Edit2,
  Trash2,
  Download,
  Users as UsersIcon,
  UtensilsCrossed,
  Loader2,
  MoreVertical,
  RefreshCw,
  X,
  Copy,
  Check,
  AlertTriangle,
  UtensilsCrossed as MenuIcon,
  ArrowLeft,
} from 'lucide-react';
import * as XLSX from 'xlsx';

type TabType = 'today' | 'restaurants' | 'users' | 'reports';

export const Admin: React.FC = () => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState<TabType>('today');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Reports states
  const [dateRangeMode, setDateRangeMode] = useState<'month' | 'custom'>('month');
  const [reportMonth, setReportMonth] = useState(new Date().getMonth() + 1);
  const [reportYear, setReportYear] = useState(new Date().getFullYear());
  const [reportStartDate, setReportStartDate] = useState('');
  const [reportEndDate, setReportEndDate] = useState('');
  const [reportCountry, setReportCountry] = useState<Country | ''>('');
  const [loadingReport, setLoadingReport] = useState(false);
  const [selectedUserIdForReport, setSelectedUserIdForReport] = useState('');
  const [userOrders, setUserOrders] = useState<LunchOrder[]>([]);
  const [topUsers, setTopUsers] = useState<any[]>([]);
  const [topRestaurants, setTopRestaurants] = useState<any[]>([]);
  const [topMenuItems, setTopMenuItems] = useState<any[]>([]);

  // Today's lunch data
  const [lunchSummary, setLunchSummary] = useState<LunchSummary | null>(null);
  const today = new Date().toISOString().split('T')[0];

  // Restaurants data
  const [restaurants, setRestaurants] = useState<Restaurant[]>([]);
  const [showRestaurantForm, setShowRestaurantForm] = useState(false);
  const [editingRestaurant, setEditingRestaurant] = useState<Restaurant | null>(null);
  const [restaurantName, setRestaurantName] = useState('');
  const [restaurantCountry, setRestaurantCountry] = useState<Country>('ARG');
  const [restaurantDescription, setRestaurantDescription] = useState('');
  const [restaurantImageUrl, setRestaurantImageUrl] = useState('');
  const [restaurantIsDefault, setRestaurantIsDefault] = useState(false);
  const [restaurantWhatsapp, setRestaurantWhatsapp] = useState('');

  // Menu items data
  const [selectedRestaurantForMenu, setSelectedRestaurantForMenu] = useState<Restaurant | null>(null);
  const [menuItems, setMenuItems] = useState<MenuItem[]>([]);
  const [showMenuItemForm, setShowMenuItemForm] = useState(false);
  const [editingMenuItem, setEditingMenuItem] = useState<MenuItem | null>(null);
  const [menuItemName, setMenuItemName] = useState('');
  const [menuItemDescription, setMenuItemDescription] = useState('');
  const [menuItemPrice, setMenuItemPrice] = useState('');
  const [menuItemCategory, setMenuItemCategory] = useState('');
  const [menuItemImageUrl, setMenuItemImageUrl] = useState('');
  const [menuItemIsCeliac, setMenuItemIsCeliac] = useState(false);

  // Users data
  const [users, setUsers] = useState<User[]>([]);
  const [showCreateUserModal, setShowCreateUserModal] = useState(false);
  const [showTempPasswordModal, setShowTempPasswordModal] = useState(false);
  const [showAssignCountryModal, setShowAssignCountryModal] = useState(false);
  const [newUserEmail, setNewUserEmail] = useState('');
  const [newUserName, setNewUserName] = useState('');
  const [newUserCountry, setNewUserCountry] = useState<Country>('ARG');
  const [tempPassword, setTempPassword] = useState('');
  const [tempPasswordEmail, setTempPasswordEmail] = useState('');
  const [editingUserRole, setEditingUserRole] = useState<string | null>(null);
  const [selectedRole, setSelectedRole] = useState<'ADMIN' | 'USER'>('USER');
  const [editingUserCountry, setEditingUserCountry] = useState<User | null>(null);
  const [selectedCountry, setSelectedCountry] = useState<Country>('ARG');
  const [openMenuUserId, setOpenMenuUserId] = useState<string | null>(null);
  const [menuPosition, setMenuPosition] = useState<'top' | 'bottom'>('bottom');
  const menuButtonRefs = useRef<{ [key: string]: HTMLButtonElement | null }>({});
  const [copiedPassword, setCopiedPassword] = useState(false);

  // Manual close modal states
  const [showManualCloseModal, setShowManualCloseModal] = useState(false);
  const [manualRestaurantName, setManualRestaurantName] = useState('');
  const [manualTotalDiners, setManualTotalDiners] = useState('');
  const [manualTotalAmount, setManualTotalAmount] = useState('');
  const [manualNotes, setManualNotes] = useState('');

  useEffect(() => {
    if (activeTab === 'today') {
      loadTodaySummary();
    } else if (activeTab === 'restaurants') {
      loadRestaurants();
    } else if (activeTab === 'users') {
      loadUsers();
    } else if (activeTab === 'reports') {
      if (users.length === 0) {
        loadUsers();
      }
    }
  }, [activeTab]);

  // Calculate menu position when it opens
  useEffect(() => {
    if (openMenuUserId && menuButtonRefs.current[openMenuUserId]) {
      const button = menuButtonRefs.current[openMenuUserId];
      const rect = button.getBoundingClientRect();
      const viewportHeight = window.innerHeight;

      // If button is in the bottom half of the viewport, show menu above
      // Otherwise show it below
      if (rect.bottom > viewportHeight / 2) {
        setMenuPosition('top');
      } else {
        setMenuPosition('bottom');
      }
    }
  }, [openMenuUserId]);

  const loadTodaySummary = async () => {
    try {
      setLoading(true);
      setError('');
      const summary = await apiClient.getLunchSummary(today);
      setLunchSummary(summary);
    } catch (err) {
      const apiError = err as ApiError;
      // 404 is expected when no lunch exists for today - don't show as error
      if (apiError.statusCode !== 404) {
        setError(apiError.message || 'Error loading lunch summary');
      }
      setLunchSummary(null);
    } finally {
      setLoading(false);
    }
  };

  const loadRestaurants = async () => {
    try {
      setLoading(true);
      setError('');
      const data = await apiClient.getAllRestaurants();
      setRestaurants(data);
    } catch (err) {
      const apiError = err as ApiError;
      setError(apiError.message || 'Error loading restaurants');
    } finally {
      setLoading(false);
    }
  };

  const loadUsers = async () => {
    try {
      setLoading(true);
      setError('');
      console.log('[Admin] Loading users...');
      const data = await apiClient.getUsers();
      console.log('[Admin] Users loaded:', data);
      setUsers(data || []);
    } catch (err) {
      const apiError = err as ApiError;
      console.error('[Admin] Error loading users:', err);
      setError(apiError.message || 'Error loading users');
      setUsers([]);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateUser = async () => {
    if (!newUserEmail || !newUserName || !newUserCountry) {
      setError('Email, name, and country are required');
      return;
    }

    try {
      setLoading(true);
      setError('');
      const result = await apiClient.createUser({
        email: newUserEmail,
        name: newUserName,
        country: newUserCountry,
      });

      setTempPassword(result.temporaryPassword);
      setTempPasswordEmail(newUserEmail);
      setShowCreateUserModal(false);
      setShowTempPasswordModal(true);
      setNewUserEmail('');
      setNewUserName('');
      setNewUserCountry('ARG');
      await loadUsers();
      setSuccess('Usuario creado exitosamente como USER. Use "Cambiar Rol" para asignar permisos de administrador.');
    } catch (err) {
      const apiError = err as ApiError;
      setError(apiError.message || 'Error creating user');
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (email: string) => {
    if (!confirm(`¿Estás seguro de resetear la contraseña de ${email}?`)) {
      return;
    }

    try {
      setLoading(true);
      setError('');
      const newPassword = await apiClient.resetUserPassword(email);
      setTempPassword(newPassword);
      setTempPasswordEmail(email);
      setShowTempPasswordModal(true);
      setSuccess('Contraseña reseteada exitosamente');
    } catch (err) {
      const apiError = err as ApiError;
      setError(apiError.message || 'Error resetting password');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveRole = async (email: string, country: Country) => {
    try {
      setLoading(true);
      setError('');
      await apiClient.updateUserRole(email, selectedRole, country);
      setEditingUserRole(null);
      await loadUsers();
      setSuccess('Rol actualizado exitosamente');
    } catch (err) {
      const apiError = err as ApiError;
      setError(apiError.message || 'Error updating role');
    } finally {
      setLoading(false);
    }
  };

  const handleDisableUser = async (email: string) => {
    if (!confirm(`¿Estás seguro de deshabilitar a ${email}?`)) {
      return;
    }

    try {
      setLoading(true);
      setError('');
      await apiClient.disableUser(email);
      await loadUsers();
      setSuccess('Usuario deshabilitado exitosamente');
    } catch (err) {
      const apiError = err as ApiError;
      setError(apiError.message || 'Error disabling user');
    } finally {
      setLoading(false);
    }
  };

  const handleEnableUser = async (email: string) => {
    if (!confirm(`¿Estás seguro de habilitar a ${email}?`)) {
      return;
    }

    try {
      setLoading(true);
      setError('');
      await apiClient.enableUser(email);
      await loadUsers();
      setSuccess('Usuario habilitado exitosamente');
    } catch (err) {
      const apiError = err as ApiError;
      setError(apiError.message || 'Error enabling user');
    } finally {
      setLoading(false);
    }
  };

  const handleAssignCountry = (user: User) => {
    setEditingUserCountry(user);
    setSelectedCountry(user.country);
    setShowAssignCountryModal(true);
    setOpenMenuUserId(null);
  };

  const handleUpdateUserCountry = async () => {
    if (!editingUserCountry) return;

    try {
      setLoading(true);
      setError('');
      await apiClient.updateUserCountry(editingUserCountry.email, selectedCountry);
      await loadUsers();
      setSuccess('País del usuario actualizado exitosamente');
      setShowAssignCountryModal(false);
      setEditingUserCountry(null);
    } catch (err) {
      const apiError = err as ApiError;
      setError(apiError.message || 'Error updating user country');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteUser = async (email: string) => {
    if (!confirm(`¿Estás seguro de eliminar permanentemente a ${email}? Esta acción no se puede deshacer.`)) {
      return;
    }

    try {
      setLoading(true);
      setError('');
      await apiClient.deleteUser(email);
      await loadUsers();
      setSuccess('Usuario eliminado exitosamente');
    } catch (err) {
      const apiError = err as ApiError;
      setError(apiError.message || 'Error deleting user');
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    setCopiedPassword(true);
    setTimeout(() => setCopiedPassword(false), 2000);
  };

  const generateWhatsAppLink = (restaurantId: string) => {
    const restaurant = restaurants.find(r => r.restaurantId === restaurantId);
    if (!restaurant?.whatsappNumber || !lunchSummary?.orders) return null;

    // Filter active orders for this restaurant
    const restaurantOrders = lunchSummary.orders.filter(
      order => order.restaurantId === restaurantId && order.status === 'active'
    );

    if (restaurantOrders.length === 0) return null;

    // Group orders by menu item and count
    const itemCounts = restaurantOrders.reduce((acc, order) => {
      const key = order.menuItemName;
      acc[key] = (acc[key] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);

    // Build message
    const itemLines = Object.entries(itemCounts)
      .map(([name, count]) => `${count}x ${name}`)
      .join('\n');

    const message = `Hola! Te hablo de CloudHesive, quiero hacerte un pedido:\n\n${itemLines}\n\nMuchas gracias!`;

    // Clean phone number (remove spaces, dashes, etc.)
    const cleanPhone = restaurant.whatsappNumber.replace(/[^0-9+]/g, '');

    return `https://wa.me/${cleanPhone}?text=${encodeURIComponent(message)}`;
  };

  const handleCreateRestaurant = async () => {
    if (!restaurantName || !restaurantCountry) {
      setError('El nombre y el país del restaurante son requeridos');
      return;
    }

    try {
      setLoading(true);
      setError('');
      await apiClient.createRestaurant({
        name: restaurantName,
        country: restaurantCountry,
        description: restaurantDescription || undefined,
        imageUrl: restaurantImageUrl || undefined,
        whatsappNumber: restaurantWhatsapp || undefined,
        isDefault: restaurantIsDefault,
      });
      setShowRestaurantForm(false);
      setRestaurantName('');
      setRestaurantCountry('ARG');
      setRestaurantDescription('');
      setRestaurantImageUrl('');
      setRestaurantWhatsapp('');
      setRestaurantIsDefault(false);
      await loadRestaurants();
      setSuccess('Restaurante creado exitosamente');
    } catch (err) {
      const apiError = err as ApiError;
      setError(apiError.message || 'Error creating restaurant');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateRestaurant = async () => {
    if (!editingRestaurant || !restaurantName || !restaurantCountry) {
      setError('El nombre y el país del restaurante son requeridos');
      return;
    }

    try {
      setLoading(true);
      setError('');
      await apiClient.updateRestaurant(editingRestaurant.restaurantId, {
        name: restaurantName,
        country: restaurantCountry,
        description: restaurantDescription || undefined,
        imageUrl: restaurantImageUrl || undefined,
        whatsappNumber: restaurantWhatsapp || undefined,
        isDefault: restaurantIsDefault,
      });
      setShowRestaurantForm(false);
      setEditingRestaurant(null);
      setRestaurantName('');
      setRestaurantCountry('ARG');
      setRestaurantDescription('');
      setRestaurantImageUrl('');
      setRestaurantWhatsapp('');
      setRestaurantIsDefault(false);
      await loadRestaurants();
      setSuccess('Restaurante actualizado exitosamente');
    } catch (err) {
      const apiError = err as ApiError;
      setError(apiError.message || 'Error updating restaurant');
    } finally {
      setLoading(false);
    }
  };

  const handleEditRestaurant = (restaurant: Restaurant) => {
    setEditingRestaurant(restaurant);
    setRestaurantName(restaurant.name);
    setRestaurantCountry(restaurant.country);
    setRestaurantDescription(restaurant.description || '');
    setRestaurantImageUrl(restaurant.imageUrl || '');
    setRestaurantWhatsapp(restaurant.whatsappNumber || '');
    setRestaurantIsDefault(restaurant.isDefault || false);
    setShowRestaurantForm(true);
  };

  const handleDeleteRestaurant = async (restaurantId: string) => {
    if (!confirm('¿Estás seguro de eliminar este restaurante?')) {
      return;
    }

    try {
      setLoading(true);
      setError('');
      await apiClient.deleteRestaurant(restaurantId);
      await loadRestaurants();
      setSuccess('Restaurante eliminado exitosamente');
    } catch (err) {
      const apiError = err as ApiError;
      setError(apiError.message || 'Error deleting restaurant');
    } finally {
      setLoading(false);
    }
  };

  const loadMenuItems = async (restaurantId: string) => {
    try {
      setLoading(true);
      setError('');
      const items = await apiClient.getAllMenuItems(restaurantId);
      setMenuItems(items);
    } catch (err) {
      const apiError = err as ApiError;
      setError(apiError.message || 'Error loading menu items');
    } finally {
      setLoading(false);
    }
  };

  const handleViewMenu = async (restaurant: Restaurant) => {
    setSelectedRestaurantForMenu(restaurant);
    await loadMenuItems(restaurant.restaurantId);
  };

  const handleCreateMenuItem = async () => {
    if (!selectedRestaurantForMenu || !menuItemName) {
      setError('El nombre del plato es requerido');
      return;
    }

    try {
      setLoading(true);
      setError('');
      await apiClient.createMenuItem(selectedRestaurantForMenu.restaurantId, {
        name: menuItemName,
        description: menuItemDescription || undefined,
        price: menuItemPrice ? parseFloat(menuItemPrice) : undefined,
        category: menuItemCategory || undefined,
        imageUrl: menuItemImageUrl || undefined,
        isCeliac: menuItemIsCeliac,
      });
      setShowMenuItemForm(false);
      setMenuItemName('');
      setMenuItemDescription('');
      setMenuItemPrice('');
      setMenuItemCategory('');
      setMenuItemImageUrl('');
      setMenuItemIsCeliac(false);
      await loadMenuItems(selectedRestaurantForMenu.restaurantId);
      setSuccess('Plato creado exitosamente');
    } catch (err) {
      const apiError = err as ApiError;
      setError(apiError.message || 'Error creating menu item');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateMenuItem = async () => {
    if (!selectedRestaurantForMenu || !editingMenuItem || !menuItemName) {
      setError('El nombre del plato es requerido');
      return;
    }

    try {
      setLoading(true);
      setError('');
      await apiClient.updateMenuItem(
        selectedRestaurantForMenu.restaurantId,
        editingMenuItem.itemId,
        {
          name: menuItemName,
          description: menuItemDescription || undefined,
          price: menuItemPrice ? parseFloat(menuItemPrice) : undefined,
          category: menuItemCategory || undefined,
          imageUrl: menuItemImageUrl || undefined,
          isCeliac: menuItemIsCeliac,
        }
      );
      setShowMenuItemForm(false);
      setEditingMenuItem(null);
      setMenuItemName('');
      setMenuItemDescription('');
      setMenuItemPrice('');
      setMenuItemCategory('');
      setMenuItemImageUrl('');
      setMenuItemIsCeliac(false);
      await loadMenuItems(selectedRestaurantForMenu.restaurantId);
      setSuccess('Plato actualizado exitosamente');
    } catch (err) {
      const apiError = err as ApiError;
      setError(apiError.message || 'Error updating menu item');
    } finally {
      setLoading(false);
    }
  };

  const handleEditMenuItem = (item: MenuItem) => {
    setEditingMenuItem(item);
    setMenuItemName(item.name);
    setMenuItemDescription(item.description || '');
    setMenuItemPrice(item.price?.toString() || '');
    setMenuItemCategory(item.category || '');
    setMenuItemImageUrl(item.imageUrl || '');
    setMenuItemIsCeliac(item.isCeliac || false);
    setShowMenuItemForm(true);
  };

  const handleDeleteMenuItem = async (itemId: string) => {
    if (!selectedRestaurantForMenu || !confirm('¿Estás seguro de eliminar este plato?')) {
      return;
    }

    try {
      setLoading(true);
      setError('');
      await apiClient.deleteMenuItem(selectedRestaurantForMenu.restaurantId, itemId);
      await loadMenuItems(selectedRestaurantForMenu.restaurantId);
      setSuccess('Plato eliminado exitosamente');
    } catch (err) {
      const apiError = err as ApiError;
      setError(apiError.message || 'Error deleting menu item');
    } finally {
      setLoading(false);
    }
  };

  const handleExportToExcel = () => {
    if (!lunchSummary || !lunchSummary.orders) {
      return;
    }

    const data = lunchSummary.orders.map((order) => ({
      Usuario: order.userName,
      Email: order.userEmail,
      Restaurante: order.restaurantName,
      'Menú': order.menuItemName,
      Precio: order.menuItemPrice ? `$${order.menuItemPrice.toFixed(2)}` : 'N/A',
      Estado: order.status === 'active' ? 'Activo' : 'Cancelado',
      'Fecha Pedido': new Date(order.createdAt).toLocaleString('es-AR'),
    }));

    // Calculate total
    const total = lunchSummary.orders.reduce((sum, order) => sum + (order.menuItemPrice || 0), 0);

    // Add total row
    data.push({
      Usuario: '',
      Email: '',
      Restaurante: '',
      'Menú': 'TOTAL',
      Precio: `$${total.toFixed(2)}`,
      Estado: '',
      'Fecha Pedido': '',
    });

    const worksheet = XLSX.utils.json_to_sheet(data);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Pedidos');

    // Set column widths
    worksheet['!cols'] = [
      { wch: 20 }, // Usuario
      { wch: 30 }, // Email
      { wch: 25 }, // Restaurante
      { wch: 35 }, // Menú
      { wch: 12 }, // Precio
      { wch: 12 }, // Estado
      { wch: 20 }, // Fecha Pedido
    ];

    XLSX.writeFile(workbook, `almuerzo-${today}.xlsx`);
    setSuccess('Excel exportado exitosamente');
  };

  const handleCloseLunch = async () => {
    if (!confirm('¿Estás seguro de que deseas cerrar el almuerzo de hoy?')) {
      return;
    }

    try {
      setLoading(true);
      setError('');
      await apiClient.closeLunch(today);
      setSuccess('Almuerzo cerrado exitosamente');
      await loadTodaySummary();
    } catch (err) {
      const apiError = err as ApiError;
      setError(apiError.message || 'Error closing lunch');
    } finally {
      setLoading(false);
    }
  };

  const handleReopenLunch = async () => {
    if (!confirm('¿Estás seguro de que deseas reabrir el almuerzo de hoy?')) {
      return;
    }

    try {
      setLoading(true);
      setError('');
      await apiClient.reopenLunch(today);
      setSuccess('Almuerzo reabierto exitosamente');
      await loadTodaySummary();
    } catch (err) {
      const apiError = err as ApiError;
      setError(apiError.message || 'Error reopening lunch');
    } finally {
      setLoading(false);
    }
  };

  const resetManualCloseForm = () => {
    setManualRestaurantName('');
    setManualTotalDiners('');
    setManualTotalAmount('');
    setManualNotes('');
  };

  const handleCloseWithManualEntry = async () => {
    if (!manualRestaurantName || !manualTotalDiners || !manualTotalAmount) {
      setError('Todos los campos obligatorios deben estar completos');
      return;
    }

    const diners = parseInt(manualTotalDiners);
    const amount = parseFloat(manualTotalAmount);

    if (isNaN(diners) || diners <= 0) {
      setError('La cantidad de comensales debe ser un número positivo');
      return;
    }

    if (isNaN(amount) || amount <= 0) {
      setError('El monto total debe ser un número positivo');
      return;
    }

    try {
      setLoading(true);
      setError('');
      await apiClient.closeLunchWithManualEntry(today, {
        restaurantName: manualRestaurantName,
        totalDiners: diners,
        totalAmount: amount,
        notes: manualNotes || undefined,
      });
      setSuccess('Almuerzo cerrado con carga manual exitosamente');
      setShowManualCloseModal(false);
      resetManualCloseForm();
      await loadTodaySummary();
    } catch (err) {
      const apiError = err as ApiError;
      setError(apiError.message || 'Error al cerrar almuerzo con carga manual');
    } finally {
      setLoading(false);
    }
  };

  const handleAdminCancelOrder = async (userId: string, userName: string) => {
    if (!confirm(`¿Estás seguro de que deseas cancelar el pedido de ${userName}?`)) {
      return;
    }

    try {
      setLoading(true);
      setError('');
      await apiClient.adminCancelOrder(today, userId);
      setSuccess(`Pedido de ${userName} cancelado exitosamente`);
      await loadTodaySummary();
    } catch (err) {
      const apiError = err as ApiError;
      setError(apiError.message || 'Error al cancelar el pedido');
    } finally {
      setLoading(false);
    }
  };

  // ==================== Reports Functions ====================

  const getDateRange = () => {
    if (dateRangeMode === 'month') {
      // Calculate start and end dates for the selected month/year
      const startDate = new Date(reportYear, reportMonth - 1, 1);
      const endDate = new Date(reportYear, reportMonth, 0); // Last day of month
      return {
        start: startDate.toISOString().split('T')[0],
        end: endDate.toISOString().split('T')[0]
      };
    } else {
      return {
        start: reportStartDate,
        end: reportEndDate
      };
    }
  };

  const filterOrdersByDateRange = (orders: LunchOrder[]) => {
    const { start, end } = getDateRange();

    return orders.filter(order => {
      if (!start && !end) return true;
      const orderDate = order.lunchDate;

      if (start && end) {
        return orderDate >= start && orderDate <= end;
      } else if (start) {
        return orderDate >= start;
      } else if (end) {
        return orderDate <= end;
      }
      return true;
    });
  };

  const loadOrdersByUser = async () => {
    if (!selectedUserIdForReport) {
      setError('Selecciona un usuario para generar el reporte');
      return;
    }

    try {
      setLoadingReport(true);
      setError('');

      const { start, end } = getDateRange();

      // Use new efficient endpoint
      const allOrders = await apiClient.getOrdersByDateRange(start, end, reportCountry || undefined);

      // Filter by selected user
      const userFilteredOrders = allOrders.filter(o => o.userId === selectedUserIdForReport);

      setUserOrders(userFilteredOrders);
      setSuccess(`Reporte generado: ${userFilteredOrders.length} pedidos encontrados`);
    } catch (err) {
      const apiError = err as ApiError;
      setError(apiError.message || 'Error al cargar el reporte');
    } finally {
      setLoadingReport(false);
    }
  };

  const loadTopUsers = async () => {
    try {
      setLoadingReport(true);
      setError('');

      const { start, end } = getDateRange();

      // Use new efficient endpoint
      const allOrders = await apiClient.getOrdersByDateRange(start, end, reportCountry || undefined);

      // Count orders by user
      const userOrderCounts: Record<string, { userId: string; userName: string; userEmail: string; orderCount: number }> = {};

      allOrders.forEach(order => {
        if (!userOrderCounts[order.userId]) {
          userOrderCounts[order.userId] = {
            userId: order.userId,
            userName: order.userName,
            userEmail: order.userEmail,
            orderCount: 0
          };
        }
        userOrderCounts[order.userId].orderCount++;
      });

      // Sort and get top 10
      const top = Object.values(userOrderCounts)
        .sort((a, b) => b.orderCount - a.orderCount)
        .slice(0, 10);

      setTopUsers(top);
      setSuccess('TOP 10 de usuarios generado exitosamente');
    } catch (err) {
      const apiError = err as ApiError;
      setError(apiError.message || 'Error al cargar TOP 10');
    } finally {
      setLoadingReport(false);
    }
  };

  const loadTopRestaurants = async () => {
    try {
      setLoadingReport(true);
      setError('');

      const { start, end } = getDateRange();

      // Use new efficient endpoint
      const allOrders = await apiClient.getOrdersByDateRange(start, end, reportCountry || undefined);

      // Count orders by restaurant
      const restaurantOrderCounts: Record<string, { restaurantId: string; restaurantName: string; orderCount: number }> = {};

      allOrders.forEach(order => {
        if (!restaurantOrderCounts[order.restaurantId]) {
          restaurantOrderCounts[order.restaurantId] = {
            restaurantId: order.restaurantId,
            restaurantName: order.restaurantName,
            orderCount: 0
          };
        }
        restaurantOrderCounts[order.restaurantId].orderCount++;
      });

      // Sort and get top 10
      const top = Object.values(restaurantOrderCounts)
        .sort((a, b) => b.orderCount - a.orderCount)
        .slice(0, 10);

      setTopRestaurants(top);
      setSuccess('TOP 10 de restaurantes generado exitosamente');
    } catch (err) {
      const apiError = err as ApiError;
      setError(apiError.message || 'Error al cargar TOP 10 de restaurantes');
    } finally {
      setLoadingReport(false);
    }
  };

  const loadTopMenuItems = async () => {
    try {
      setLoadingReport(true);
      setError('');

      const { start, end } = getDateRange();

      // Use new efficient endpoint
      const allOrders = await apiClient.getOrdersByDateRange(start, end, reportCountry || undefined);

      // Count orders by menu item
      const menuItemOrderCounts: Record<string, { restaurantId: string; restaurantName: string; menuItemId: string; menuItemName: string; orderCount: number }> = {};

      allOrders.forEach(order => {
        const key = `${order.restaurantId}-${order.menuItemId}`;
        if (!menuItemOrderCounts[key]) {
          menuItemOrderCounts[key] = {
            restaurantId: order.restaurantId,
            restaurantName: order.restaurantName,
            menuItemId: order.menuItemId,
            menuItemName: order.menuItemName,
            orderCount: 0
          };
        }
        menuItemOrderCounts[key].orderCount++;
      });

      // Sort and get top 10
      const top = Object.values(menuItemOrderCounts)
        .sort((a, b) => b.orderCount - a.orderCount)
        .slice(0, 10);

      setTopMenuItems(top);
      setSuccess('TOP 10 de platos generado exitosamente');
    } catch (err) {
      const apiError = err as ApiError;
      setError(apiError.message || 'Error al cargar TOP 10 de platos');
    } finally {
      setLoadingReport(false);
    }
  };

  return (
    <div className="max-w-7xl mx-auto animate-fade-in">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-foreground mb-2">{t('admin.title')}</h1>
        <p className="text-muted-foreground">{t('admin.subtitle')}</p>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-6 border-b border-border">
        {([
          { key: 'today',       icon: UtensilsCrossed, label: t('admin.tabToday')       },
          { key: 'restaurants', icon: ChefHat,         label: t('admin.tabRestaurants') },
          { key: 'users',       icon: UsersIcon,       label: t('admin.tabUsers')       },
          { key: 'reports',     icon: Download,        label: t('admin.tabReports')     },
        ] as const).map(({ key, icon: Icon, label }) => (
          <button
            key={key}
            onClick={() => setActiveTab(key)}
            className={`px-6 py-3 font-medium transition-colors border-b-2 ${
              activeTab === key
                ? 'border-primary text-primary'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            <div className="flex items-center gap-2">
              <Icon className="w-5 h-5" />
              {label}
            </div>
          </button>
        ))}
      </div>

      {/* Alerts */}
      {error && (
        <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-4 mb-6">
          <p className="text-destructive">{error}</p>
        </div>
      )}

      {success && (
        <div className="bg-success/10 border border-success/20 rounded-lg p-4 mb-6">
          <p className="text-success">{success}</p>
        </div>
      )}

      {/* Content */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-primary/80" />
        </div>
      ) : (
        <>
          {/* Today's Lunch Tab */}
          {activeTab === 'today' && !lunchSummary && (
            <div className="bg-card rounded-lg shadow-sm border border-border p-12 text-center">
              <UtensilsCrossed className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-xl font-bold text-foreground mb-2">
                No hay almuerzo programado para hoy
              </h3>
              <p className="text-muted-foreground mb-6">
                Los almuerzos se gestionan automáticamente cuando los usuarios hacen sus pedidos.
              </p>
              <div className="bg-primary/5 border-t-2 border-primary/20 rounded-lg p-4 max-w-md mx-auto">
                <p className="text-sm text-primary">
                  <strong>Nota:</strong> El almuerzo del día se crea automáticamente cuando el primer usuario realiza un pedido.
                  Una vez que haya pedidos, podrás verlos y gestionarlos desde aquí.
                </p>
              </div>
            </div>
          )}

          {activeTab === 'today' && lunchSummary && (
            <div className="space-y-6">
              {/* Summary Cards */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="bg-card rounded-lg shadow-sm border border-border p-6">
                  <p className="text-muted-foreground text-sm mb-1">
                    {lunchSummary.isManualEntry ? 'Comensales' : 'Total Pedidos'}
                  </p>
                  <p className="text-3xl font-bold text-foreground">{lunchSummary.totalOrders}</p>
                </div>
                <div className="bg-card rounded-lg shadow-sm border border-border p-6">
                  <p className="text-muted-foreground text-sm mb-1">Estado</p>
                  <div className="flex items-center gap-2">
                    <p className="text-xl font-bold text-foreground capitalize">{lunchSummary.status}</p>
                    {lunchSummary.isManualEntry && (
                      <span className="px-2 py-1 bg-warning/10 text-warning text-xs rounded-full font-medium">
                        Carga Manual
                      </span>
                    )}
                  </div>
                </div>
                <div className="bg-card rounded-lg shadow-sm border border-border p-6">
                  <p className="text-muted-foreground text-sm mb-1">
                    {lunchSummary.isManualEntry ? 'Monto Total' : 'Restaurantes'}
                  </p>
                  <p className="text-3xl font-bold text-foreground">
                    {lunchSummary.isManualEntry && lunchSummary.manualEntry
                      ? `$${lunchSummary.manualEntry.totalAmount.toLocaleString()}`
                      : lunchSummary.ordersByRestaurant.length}
                  </p>
                </div>
              </div>

              {/* Manual Entry Details */}
              {lunchSummary.isManualEntry && lunchSummary.manualEntry && (
                <div className="bg-warning/10 border border-warning/20 rounded-lg p-6">
                  <h3 className="text-lg font-bold text-warning mb-4">Detalles de Carga Manual</h3>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div>
                      <p className="text-sm text-muted-foreground">Restaurante</p>
                      <p className="font-medium text-foreground">{lunchSummary.manualEntry.restaurantName}</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Comensales</p>
                      <p className="font-medium text-foreground">{lunchSummary.manualEntry.totalDiners}</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Monto Total</p>
                      <p className="font-medium text-foreground">${lunchSummary.manualEntry.totalAmount.toLocaleString()}</p>
                    </div>
                    {lunchSummary.manualEntry.notes && (
                      <div>
                        <p className="text-sm text-muted-foreground">Notas</p>
                        <p className="font-medium text-foreground">{lunchSummary.manualEntry.notes}</p>
                      </div>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground mt-4">
                    Registrado por: {lunchSummary.manualEntry.createdByName}
                  </p>
                </div>
              )}

              {/* Actions */}
              <div className="flex gap-4">
                <button
                  onClick={handleExportToExcel}
                  disabled={!lunchSummary.orders || lunchSummary.orders.length === 0}
                  className="flex items-center gap-2 px-6 py-3 bg-success text-white rounded-lg hover:bg-success/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Download className="w-5 h-5" />
                  Exportar a Excel
                </button>

                {lunchSummary.status === 'open' && (
                  <>
                    <button
                      onClick={handleCloseLunch}
                      className="flex items-center gap-2 px-6 py-3 bg-destructive text-destructive-foreground rounded-lg hover:bg-destructive/90 transition-colors"
                    >
                      Cerrar Almuerzo
                    </button>
                    <button
                      onClick={() => setShowManualCloseModal(true)}
                      className="flex items-center gap-2 px-6 py-3 bg-warning text-warning-foreground rounded-lg hover:bg-warning/90 transition-colors"
                    >
                      <Plus className="w-5 h-5" />
                      Cerrar con Carga Manual
                    </button>
                  </>
                )}

                {lunchSummary.status === 'closed' && (
                  <button
                    onClick={handleReopenLunch}
                    className="flex items-center gap-2 px-6 py-3 bg-primary text-primary-foreground rounded-lg hover:bg-opacity-90 transition-colors"
                  >
                    <RefreshCw className="w-5 h-5" />
                    Reabrir Almuerzo
                  </button>
                )}
              </div>

              {/* Orders by Restaurant */}
              <div className="bg-card rounded-lg shadow-sm border border-border p-6">
                <h3 className="text-lg font-bold text-foreground mb-4">Pedidos por Restaurante</h3>
                <div className="space-y-3">
                  {lunchSummary.ordersByRestaurant.map((group) => {
                    const whatsappLink = lunchSummary.status === 'closed' ? generateWhatsAppLink(group.restaurantId) : null;
                    return (
                      <div
                        key={group.restaurantId}
                        className="flex justify-between items-center p-4 bg-muted/50 rounded-lg"
                      >
                        <span className="font-medium text-foreground">{group.restaurantName}</span>
                        <div className="flex items-center gap-3">
                          <span className="px-4 py-1 bg-primary text-primary-foreground rounded-full text-sm font-medium">
                            {group.count} pedidos
                          </span>
                          {whatsappLink && (
                            <a
                              href={whatsappLink}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="flex items-center gap-2 px-3 py-1 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors text-sm font-medium"
                              title="Enviar pedido por WhatsApp"
                            >
                              <svg
                                className="w-4 h-4"
                                fill="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                              </svg>
                              WhatsApp
                            </a>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Orders List */}
              {lunchSummary.orders && lunchSummary.orders.length > 0 && (
                <div className="bg-card rounded-lg shadow-sm border border-border p-6">
                  <h3 className="text-lg font-bold text-foreground mb-4">Lista de Pedidos</h3>
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-border">
                          <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">
                            Usuario
                          </th>
                          <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">
                            Restaurante
                          </th>
                          <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">
                            Menú
                          </th>
                          <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">
                            Precio
                          </th>
                          <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">
                            Estado
                          </th>
                          <th className="text-center py-3 px-4 text-sm font-medium text-muted-foreground">
                            Acciones
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {lunchSummary.orders.map((order) => (
                          <tr key={order.orderId} className="border-b border-gray-100">
                            <td className="py-3 px-4">{order.userName}</td>
                            <td className="py-3 px-4">{order.restaurantName}</td>
                            <td className="py-3 px-4">{order.menuItemName}</td>
                            <td className="py-3 px-4 font-medium text-success">
                              {order.menuItemPrice ? `$${order.menuItemPrice.toFixed(2)}` : 'N/A'}
                            </td>
                            <td className="py-3 px-4">
                              <span
                                className={`px-3 py-1 rounded-full text-xs font-medium ${
                                  order.status === 'active'
                                    ? 'bg-success/10 text-success'
                                    : 'bg-red-100 text-destructive'
                                }`}
                              >
                                {order.status === 'active' ? 'Activo' : 'Cancelado'}
                              </span>
                            </td>
                            <td className="py-3 px-4 text-center">
                              {order.status === 'active' && (
                                <button
                                  onClick={() => handleAdminCancelOrder(order.userId, order.userName)}
                                  disabled={loading}
                                  className="p-1 text-destructive hover:text-destructive hover:bg-red-100 rounded transition-colors disabled:opacity-50"
                                  title={`Cancelar pedido de ${order.userName}`}
                                >
                                  <X className="w-5 h-5" />
                                </button>
                              )}
                            </td>
                          </tr>
                        ))}
                        {/* Total row */}
                        <tr className="border-t-2 border-blue-200 bg-primary/5">
                          <td className="py-3 px-4" colSpan={3}>
                            <span className="font-bold text-foreground">Total:</span>
                          </td>
                          <td className="py-3 px-4 font-bold text-success text-lg">
                            ${lunchSummary.orders.reduce((sum, order) => sum + (order.menuItemPrice || 0), 0).toFixed(2)}
                          </td>
                          <td className="py-3 px-4" colSpan={2}></td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Restaurants Tab */}
          {activeTab === 'restaurants' && !selectedRestaurantForMenu && (
            <div className="space-y-6">
              <div className="flex justify-between items-center">
                <h2 className="text-xl font-bold text-foreground">Gestión de Restaurantes</h2>
                <button
                  onClick={() => {
                    setShowRestaurantForm(true);
                    setEditingRestaurant(null);
                    setRestaurantName('');
                    setRestaurantDescription('');
                    setRestaurantImageUrl('');
                    setRestaurantWhatsapp('');
                  }}
                  className="px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-blue-600 transition-colors flex items-center gap-2"
                >
                  <Plus className="w-4 h-4" />
                  Agregar Restaurante
                </button>
              </div>

              {restaurants.length === 0 ? (
                <div className="bg-card rounded-lg shadow-sm border border-border p-12 text-center">
                  <ChefHat className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
                  <h3 className="text-xl font-bold text-foreground mb-2">No hay restaurantes</h3>
                  <p className="text-muted-foreground">Agrega el primer restaurante</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {restaurants.map((restaurant) => (
                    <div
                      key={restaurant.restaurantId}
                      className="bg-white border border-border rounded-lg p-6 hover:shadow-sm transition-shadow"
                    >
                      <div className="flex justify-between items-start mb-3">
                        <h3 className="font-bold text-foreground text-lg">{restaurant.name}</h3>
                        <div className="flex gap-2">
                          <button
                            onClick={() => handleEditRestaurant(restaurant)}
                            className="text-blue-600 hover:text-primary"
                          >
                            <Edit2 className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDeleteRestaurant(restaurant.restaurantId)}
                            className="text-destructive hover:text-destructive"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                      {restaurant.description && (
                        <p className="text-sm text-muted-foreground mb-4">{restaurant.description}</p>
                      )}
                      <button
                        onClick={() => handleViewMenu(restaurant)}
                        className="w-full mt-2 px-3 py-2 bg-primary/5 text-blue-700 rounded-lg hover:bg-primary/10 transition-colors flex items-center justify-center gap-2"
                      >
                        <MenuIcon className="w-4 h-4" />
                        Ver Menú
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Menu Items View (when restaurant is selected) */}
          {activeTab === 'restaurants' && selectedRestaurantForMenu && (
            <div className="space-y-6">
              <div>
                <button
                  onClick={() => {
                    setSelectedRestaurantForMenu(null);
                    setMenuItems([]);
                    setShowMenuItemForm(false);
                    setEditingMenuItem(null);
                  }}
                  className="flex items-center gap-2 text-primary hover:text-blue-700 mb-4"
                >
                  <ArrowLeft className="w-5 h-5" />
                  Volver a Restaurantes
                </button>
                <h2 className="text-2xl font-bold text-foreground">
                  Menú de {selectedRestaurantForMenu.name}
                </h2>
              </div>

              <div className="bg-card rounded-lg shadow-sm border border-border p-6">
                <div className="flex justify-between items-center mb-6">
                  <h3 className="text-xl font-bold text-foreground">Platos</h3>
                  <button
                    onClick={() => {
                      setShowMenuItemForm(true);
                      setEditingMenuItem(null);
                      setMenuItemName('');
                      setMenuItemDescription('');
                      setMenuItemPrice('');
                      setMenuItemCategory('');
                      setMenuItemImageUrl('');
                      setMenuItemIsCeliac(false);
                    }}
                    className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    <Plus className="w-5 h-5" />
                    Agregar Plato
                  </button>
                </div>

                {menuItems.length === 0 ? (
                  <div className="text-center py-12">
                    <MenuIcon className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
                    <h3 className="text-xl font-bold text-foreground mb-2">No hay platos</h3>
                    <p className="text-muted-foreground">Agrega el primer plato al menú</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {menuItems.map((item) => (
                      <div
                        key={item.itemId}
                        className="border border-border rounded-lg p-4 hover:shadow-sm transition-shadow"
                      >
                        <div className="flex justify-between items-start mb-2">
                          <h3 className="font-bold text-foreground">{item.name}</h3>
                          <div className="flex gap-2">
                            <button
                              onClick={() => handleEditMenuItem(item)}
                              className="text-blue-600 hover:text-primary"
                            >
                              <Edit2 className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleDeleteMenuItem(item.itemId)}
                              className="text-destructive hover:text-destructive"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                        {item.description && (
                          <p className="text-sm text-muted-foreground mb-2">{item.description}</p>
                        )}
                        {item.price && (
                          <p className="text-sm font-medium text-success">
                            ${item.price.toFixed(2)}
                          </p>
                        )}
                        {item.category && (
                          <p className="text-xs text-muted-foreground mt-2">
                            Categoría: {item.category}
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Users Tab */}
          {activeTab === 'users' && (
            <div className="space-y-6">
              <div className="flex justify-between items-center">
                <h2 className="text-xl font-bold text-foreground">Gestión de Usuarios</h2>
                <div className="flex gap-3">
                  <button
                    onClick={loadUsers}
                    disabled={loading}
                    className="px-4 py-2 border border-b-2 border-primary text-primary rounded-lg hover:bg-primary/5 transition-colors flex items-center gap-2"
                  >
                    <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                    Actualizar
                  </button>
                  <button
                    onClick={() => setShowCreateUserModal(true)}
                    className="px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-blue-600 transition-colors flex items-center gap-2"
                  >
                    <Plus className="w-4 h-4" />
                    Crear Usuario
                  </button>
                </div>
              </div>

              <div className="bg-card rounded-lg shadow-sm border border-border overflow-hidden">
                <table className="w-full">
                  <thead className="bg-muted/50">
                    <tr>
                      <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">
                        Usuario
                      </th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">
                        Email
                      </th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">
                        Rol
                      </th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">
                        Estado
                      </th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">
                        Fecha Creación
                      </th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">
                        Acciones
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {users?.map((user) => (
                      <tr key={user.userId} className="border-b border-gray-100 hover:bg-muted/50">
                        <td className="py-3 px-4">
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 bg-primary text-primary-foreground rounded-full flex items-center justify-center font-medium">
                              {user.name.charAt(0).toUpperCase()}
                            </div>
                            <span className="font-medium">{user.name}</span>
                          </div>
                        </td>
                        <td className="py-3 px-4 text-muted-foreground">{user.email}</td>
                        <td className="py-3 px-4">
                          {editingUserRole === user.email ? (
                            <div className="flex items-center gap-2">
                              <select
                                value={selectedRole}
                                onChange={(e) => setSelectedRole(e.target.value as 'ADMIN' | 'USER')}
                                className="px-2 py-1 border border-input rounded text-sm"
                              >
                                <option value="USER">USER</option>
                                <option value="ADMIN">ADMIN</option>
                              </select>
                              <button
                                onClick={() => handleSaveRole(user.email, user.country)}
                                className="px-2 py-1 bg-green-500 text-white rounded text-sm hover:bg-green-600"
                              >
                                <Check className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => setEditingUserRole(null)}
                                className="px-2 py-1 bg-muted/500 text-white rounded text-sm hover:bg-gray-600"
                              >
                                <X className="w-4 h-4" />
                              </button>
                            </div>
                          ) : (
                            <button
                              onClick={() => {
                                setEditingUserRole(user.email);
                                setSelectedRole(user.role);
                              }}
                              className={`px-3 py-1 rounded-full text-xs font-medium cursor-pointer hover:opacity-80 ${
                                user.role === 'ADMIN'
                                  ? 'bg-purple-100 text-purple-800'
                                  : 'bg-gray-100 text-gray-800'
                              }`}
                            >
                              {user.role}
                            </button>
                          )}
                        </td>
                        <td className="py-3 px-4">
                          <span
                            className={`px-3 py-1 rounded-full text-xs font-medium ${
                              user.enabled === false
                                ? 'bg-red-100 text-destructive'
                                : user.status === 'FORCE_CHANGE_PASSWORD'
                                ? 'bg-yellow-100 text-yellow-800'
                                : user.status === 'CONFIRMED'
                                ? 'bg-success/10 text-success'
                                : 'bg-gray-100 text-gray-800'
                            }`}
                          >
                            {user.enabled === false
                              ? 'Deshabilitado'
                              : user.status === 'FORCE_CHANGE_PASSWORD'
                              ? 'Cambiar contraseña'
                              : user.status === 'CONFIRMED'
                              ? 'Activo'
                              : user.status || 'Desconocido'}
                          </span>
                        </td>
                        <td className="py-3 px-4 text-muted-foreground text-sm">
                          {user.createdAt ? new Date(user.createdAt).toLocaleDateString('es-AR') : '-'}
                        </td>
                        <td className="py-3 px-4">
                          <div className="relative">
                            <button
                              ref={(el) => (menuButtonRefs.current[user.userId] = el)}
                              onClick={() => setOpenMenuUserId(openMenuUserId === user.userId ? null : user.userId)}
                              className="p-1 hover:bg-gray-200 rounded"
                            >
                              <MoreVertical className="w-5 h-5 text-muted-foreground" />
                            </button>
                            {openMenuUserId === user.userId && (
                              <div className={`absolute right-0 w-56 bg-white rounded-lg shadow-lg border border-border z-10 ${
                                menuPosition === 'top' ? 'bottom-full mb-2' : 'mt-2'
                              }`}>
                                <button
                                  onClick={() => {
                                    setEditingUserRole(user.email);
                                    setSelectedRole(user.role);
                                    setOpenMenuUserId(null);
                                  }}
                                  className="w-full text-left px-4 py-2 hover:bg-muted/50 flex items-center gap-2"
                                >
                                  <Edit2 className="w-4 h-4" />
                                  Cambiar Rol
                                </button>
                                <button
                                  onClick={() => {
                                    handleResetPassword(user.email);
                                    setOpenMenuUserId(null);
                                  }}
                                  className="w-full text-left px-4 py-2 hover:bg-muted/50 flex items-center gap-2"
                                >
                                  <RefreshCw className="w-4 h-4" />
                                  Resetear Contraseña
                                </button>
                                <button
                                  onClick={() => handleAssignCountry(user)}
                                  className="w-full text-left px-4 py-2 hover:bg-muted/50 flex items-center gap-2"
                                >
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 21v-4m0 0V5a2 2 0 012-2h6.5l1 1H21l-3 6 3 6h-8.5l-1-1H5a2 2 0 00-2 2zm9-13.5V9" />
                                  </svg>
                                  Asignar País
                                </button>
                                <div className="border-t border-border"></div>
                                {user.enabled !== false ? (
                                  <button
                                    onClick={() => {
                                      handleDisableUser(user.email);
                                      setOpenMenuUserId(null);
                                    }}
                                    className="w-full text-left px-4 py-2 hover:bg-muted/50 flex items-center gap-2 text-warning"
                                  >
                                    <X className="w-4 h-4" />
                                    Deshabilitar Usuario
                                  </button>
                                ) : (
                                  <button
                                    onClick={() => {
                                      handleEnableUser(user.email);
                                      setOpenMenuUserId(null);
                                    }}
                                    className="w-full text-left px-4 py-2 hover:bg-muted/50 flex items-center gap-2 text-success"
                                  >
                                    <Check className="w-4 h-4" />
                                    Habilitar Usuario
                                  </button>
                                )}
                                <div className="border-t border-border"></div>
                                <button
                                  onClick={() => {
                                    handleDeleteUser(user.email);
                                    setOpenMenuUserId(null);
                                  }}
                                  className="w-full text-left px-4 py-2 hover:bg-muted/50 flex items-center gap-2 text-destructive"
                                >
                                  <Trash2 className="w-4 h-4" />
                                  Eliminar Usuario
                                </button>
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {users.length === 0 && (
                  <div className="text-center py-8 text-muted-foreground">
                    No hay usuarios para mostrar
                  </div>
                )}
              </div>
            </div>
          )}
        </>
      )}

      {/* Reports Tab */}
      {activeTab === 'reports' && (
        <div className="space-y-6">
          <h2 className="text-xl font-bold text-foreground">Reportes y Estadísticas</h2>

          {/* Country Selector */}
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold text-foreground mb-4">Seleccionar País</h3>
            <select
              value={reportCountry}
              onChange={(e) => setReportCountry(e.target.value as Country | '')}
              className="w-full max-w-xs px-4 py-2 border border-input rounded-lg focus:outline-none focus:ring-2 focus:ring-ring"
            >
              <option value="">Todos los países</option>
              <option value="ARG">Argentina</option>
              <option value="CHL">Chile</option>
              <option value="COL">Colombia</option>
            </select>
          </div>

          {/* Month and Year Selector */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-foreground">Seleccionar Período</h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Opción 1: Por Mes y Año */}
              <div
                onClick={() => setDateRangeMode('month')}
                className={`bg-white rounded-lg shadow p-6 cursor-pointer border-2 transition-all ${
                  dateRangeMode === 'month'
                    ? 'border-primary'
                    : 'border-border opacity-50'
                }`}
              >
                <div className="flex items-center justify-between mb-4">
                  <h4 className="text-md font-semibold text-foreground">Por Mes y Año</h4>
                  <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                    dateRangeMode === 'month'
                      ? 'border-primary bg-blue-bright'
                      : 'border-input'
                  }`}>
                    {dateRangeMode === 'month' && (
                      <div className="w-2 h-2 bg-white rounded-full"></div>
                    )}
                  </div>
                </div>
                <div className="flex gap-4">
                  <div className="flex-1">
                    <label className="block text-sm font-medium text-foreground mb-2">Mes</label>
                    <select
                      value={reportMonth}
                      onChange={(e) => setReportMonth(parseInt(e.target.value))}
                      disabled={dateRangeMode !== 'month'}
                      className="w-full px-4 py-2 border border-input rounded-lg focus:outline-none focus:ring-2 focus:ring-ring disabled:bg-gray-100 disabled:cursor-not-allowed"
                    >
                      {['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'].map((month, idx) => (
                        <option key={idx} value={idx + 1}>{month}</option>
                      ))}
                    </select>
                  </div>
                  <div className="flex-1">
                    <label className="block text-sm font-medium text-foreground mb-2">Año</label>
                    <select
                      value={reportYear}
                      onChange={(e) => setReportYear(parseInt(e.target.value))}
                      disabled={dateRangeMode !== 'month'}
                      className="w-full px-4 py-2 border border-input rounded-lg focus:outline-none focus:ring-2 focus:ring-ring disabled:bg-gray-100 disabled:cursor-not-allowed"
                    >
                      {[2024, 2025, 2026].map((year) => (
                        <option key={year} value={year}>{year}</option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              {/* Opción 2: Rango Personalizado */}
              <div
                onClick={() => setDateRangeMode('custom')}
                className={`bg-white rounded-lg shadow p-6 cursor-pointer border-2 transition-all ${
                  dateRangeMode === 'custom'
                    ? 'border-primary'
                    : 'border-border opacity-50'
                }`}
              >
                <div className="flex items-center justify-between mb-4">
                  <h4 className="text-md font-semibold text-foreground">Rango Personalizado</h4>
                  <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                    dateRangeMode === 'custom'
                      ? 'border-primary bg-blue-bright'
                      : 'border-input'
                  }`}>
                    {dateRangeMode === 'custom' && (
                      <div className="w-2 h-2 bg-white rounded-full"></div>
                    )}
                  </div>
                </div>
                <div className="flex gap-4">
                  <div className="flex-1">
                    <label className="block text-sm font-medium text-foreground mb-2">Desde</label>
                    <input
                      type="date"
                      value={reportStartDate}
                      onChange={(e) => setReportStartDate(e.target.value)}
                      disabled={dateRangeMode !== 'custom'}
                      className="w-full px-4 py-2 border border-input rounded-lg focus:outline-none focus:ring-2 focus:ring-ring disabled:bg-gray-100 disabled:cursor-not-allowed"
                    />
                  </div>
                  <div className="flex-1">
                    <label className="block text-sm font-medium text-foreground mb-2">Hasta</label>
                    <input
                      type="date"
                      value={reportEndDate}
                      onChange={(e) => setReportEndDate(e.target.value)}
                      disabled={dateRangeMode !== 'custom'}
                      className="w-full px-4 py-2 border border-input rounded-lg focus:outline-none focus:ring-2 focus:ring-ring disabled:bg-gray-100 disabled:cursor-not-allowed"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Report 1: Orders by User */}
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold text-foreground mb-4">Pedidos por Usuario</h3>
            <div className="flex gap-4 items-end mb-4">
              <div className="flex-1">
                <label className="block text-sm font-medium text-foreground mb-2">Seleccionar Usuario</label>
                <select
                  value={selectedUserIdForReport}
                  onChange={(e) => setSelectedUserIdForReport(e.target.value)}
                  className="w-full px-4 py-2 border border-input rounded-lg focus:outline-none focus:ring-2 focus:ring-ring"
                >
                  <option value="">-- Seleccionar Usuario --</option>
                  {users.map((u) => (
                    <option key={u.userId} value={u.userId}>{u.name} ({u.email})</option>
                  ))}
                </select>
              </div>
              <button
                onClick={loadOrdersByUser}
                disabled={loadingReport || !selectedUserIdForReport}
                className="px-6 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-opacity-90 transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed"
              >
                {loadingReport ? 'Generando...' : 'Generar Reporte'}
              </button>
            </div>
            {userOrders.length === 0 && selectedUserIdForReport && !loadingReport && (
              <div className="text-center py-8 text-muted-foreground">
                <p>No hay pedidos para este usuario en el período seleccionado.</p>
              </div>
            )}
            {userOrders.length > 0 && (
              <div className="mt-4 overflow-x-auto">
                <p className="text-sm text-muted-foreground mb-2">Total de pedidos: <strong>{userOrders.length}</strong></p>
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-muted/50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Fecha</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Restaurante</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Plato</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Estado</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {userOrders.map((order) => (
                      <tr key={order.orderId}>
                        <td className="px-6 py-4 whitespace-nowrap text-sm">{new Date(order.lunchDate).toLocaleDateString('es-AR')}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm">{order.restaurantName}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm">{order.menuItemName}</td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`px-2 py-1 text-xs rounded-full ${
                            order.status === 'active' ? 'bg-success/10 text-green-700' : 'bg-red-100 text-red-700'
                          }`}>
                            {order.status === 'active' ? 'Activo' : 'Cancelado'}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Report 2: TOP 10 Users */}
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold text-foreground mb-4">TOP 10 de Usuarios</h3>
            <button
              onClick={loadTopUsers}
              disabled={loadingReport}
              className="px-6 py-2 bg-turquoise text-white rounded-lg hover:bg-opacity-90 transition-colors disabled:bg-gray-300 mb-4"
            >
              {loadingReport ? 'Generando...' : 'Generar TOP 10'}
            </button>
            {topUsers.length === 0 && !loadingReport && (
              <div className="text-center py-8 text-muted-foreground">
                <p>No hay datos para mostrar. Haz clic en "Generar TOP 10" para ver los resultados del período seleccionado.</p>
              </div>
            )}
            {topUsers.length > 0 && (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-muted/50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Posición</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Usuario</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Email</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Pedidos</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {topUsers.map((u, idx) => (
                      <tr key={u.userId} className={idx < 3 ? 'bg-yellow-50' : ''}>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`px-3 py-1 rounded-full font-bold ${
                            idx === 0 ? 'bg-yellow-400 text-foreground' :
                            idx === 1 ? 'bg-gray-300 text-foreground' :
                            idx === 2 ? 'bg-orange-300 text-foreground' :
                            'bg-gray-100 text-muted-foreground'
                          }`}>
                            #{idx + 1}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">{u.userName}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-muted-foreground">{u.userEmail}</td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="px-3 py-1 bg-primary text-primary-foreground rounded-full font-bold">{u.orderCount}</span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Report 3: TOP 10 Restaurants */}
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold text-foreground mb-4">TOP 10 de Restaurantes</h3>
            <button
              onClick={loadTopRestaurants}
              disabled={loadingReport}
              className="px-6 py-2 bg-success text-white rounded-lg hover:bg-opacity-90 transition-colors disabled:bg-gray-300 mb-4"
            >
              {loadingReport ? 'Generando...' : 'Generar TOP 10'}
            </button>
            {topRestaurants.length === 0 && !loadingReport && (
              <div className="text-center py-8 text-muted-foreground">
                <p>No hay datos para mostrar. Haz clic en "Generar TOP 10" para ver los restaurantes más populares del período seleccionado.</p>
              </div>
            )}
            {topRestaurants.length > 0 && (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-muted/50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Posición</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Restaurante</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Pedidos</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {topRestaurants.map((r, idx) => (
                      <tr key={r.restaurantId} className={idx < 3 ? 'bg-yellow-50' : ''}>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`px-3 py-1 rounded-full font-bold ${
                            idx === 0 ? 'bg-yellow-400 text-foreground' :
                            idx === 1 ? 'bg-gray-300 text-foreground' :
                            idx === 2 ? 'bg-orange-300 text-foreground' :
                            'bg-gray-100 text-muted-foreground'
                          }`}>
                            #{idx + 1}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">{r.restaurantName}</td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="px-3 py-1 bg-success text-white rounded-full font-bold">{r.orderCount}</span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Report 4: TOP 10 Menu Items */}
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold text-foreground mb-4">TOP 10 de Pedidos (Platos)</h3>
            <button
              onClick={loadTopMenuItems}
              disabled={loadingReport}
              className="px-6 py-2 bg-purple text-white rounded-lg hover:bg-opacity-90 transition-colors disabled:bg-gray-300 mb-4"
            >
              {loadingReport ? 'Generando...' : 'Generar TOP 10'}
            </button>
            {topMenuItems.length === 0 && !loadingReport && (
              <div className="text-center py-8 text-muted-foreground">
                <p>No hay datos para mostrar. Haz clic en "Generar TOP 10" para ver los platos más pedidos del período seleccionado.</p>
              </div>
            )}
            {topMenuItems.length > 0 && (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-muted/50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Posición</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Plato</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Restaurante</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Pedidos</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {topMenuItems.map((item, idx) => (
                      <tr key={`${item.restaurantId}-${item.menuItemId}`} className={idx < 3 ? 'bg-yellow-50' : ''}>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`px-3 py-1 rounded-full font-bold ${
                            idx === 0 ? 'bg-yellow-400 text-foreground' :
                            idx === 1 ? 'bg-gray-300 text-foreground' :
                            idx === 2 ? 'bg-orange-300 text-foreground' :
                            'bg-gray-100 text-muted-foreground'
                          }`}>
                            #{idx + 1}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">{item.menuItemName}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-muted-foreground">{item.restaurantName}</td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="px-3 py-1 bg-purple text-white rounded-full font-bold">{item.orderCount}</span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Create User Modal */}
      {showCreateUserModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold text-foreground">Crear Nuevo Usuario</h3>
              <button
                onClick={() => setShowCreateUserModal(false)}
                className="text-muted-foreground hover:text-foreground"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">
                  Email *
                </label>
                <input
                  type="email"
                  value={newUserEmail}
                  onChange={(e) => setNewUserEmail(e.target.value)}
                  placeholder="usuario@cloudhesive.com"
                  className="w-full px-3 py-2 border border-input rounded-lg focus:outline-none focus:ring-2 focus:ring-ring"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-1">
                  Nombre *
                </label>
                <input
                  type="text"
                  value={newUserName}
                  onChange={(e) => setNewUserName(e.target.value)}
                  placeholder="Nombre completo"
                  className="w-full px-3 py-2 border border-input rounded-lg focus:outline-none focus:ring-2 focus:ring-ring"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-1">
                  País *
                </label>
                <select
                  value={newUserCountry}
                  onChange={(e) => setNewUserCountry(e.target.value as Country)}
                  className="w-full px-3 py-2 border border-input rounded-lg focus:outline-none focus:ring-2 focus:ring-ring"
                >
                  <option value="ARG">Argentina</option>
                  <option value="CHL">Chile</option>
                  <option value="COL">Colombia</option>
                </select>
              </div>

              <div className="bg-primary/5 border-t-2 border-primary/20 rounded-lg p-3 text-sm text-primary">
                <p className="font-medium mb-1">Información importante:</p>
                <ul className="list-disc list-inside space-y-1">
                  <li>El usuario se creará con rol USER por defecto</li>
                  <li>Para asignar permisos de administrador, use "Cambiar Rol" después de crear el usuario</li>
                  <li>Se generará una contraseña temporal que deberá cambiar en su primer inicio de sesión</li>
                </ul>
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={() => setShowCreateUserModal(false)}
                disabled={loading}
                className="flex-1 px-4 py-2 border border-input text-foreground rounded-lg hover:bg-muted/50 transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={handleCreateUser}
                disabled={loading || !newUserEmail || !newUserName}
                className="flex-1 px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-blue-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Creando...
                  </>
                ) : (
                  'Crear Usuario'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Temporary Password Modal */}
      {showTempPasswordModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold text-foreground">Contraseña Temporal</h3>
              <button
                onClick={() => {
                  setShowTempPasswordModal(false);
                  setTempPassword('');
                  setTempPasswordEmail('');
                  setCopiedPassword(false);
                }}
                className="text-muted-foreground hover:text-foreground"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-4">
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-yellow-800">
                  <p className="font-medium mb-1">¡Importante!</p>
                  <p>Esta contraseña solo se mostrará una vez. Asegúrate de copiarla y enviarla al usuario.</p>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-1">
                  Usuario
                </label>
                <div className="px-3 py-2 bg-muted/50 border border-input rounded-lg text-foreground">
                  {tempPasswordEmail}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-1">
                  Contraseña Temporal
                </label>
                <div className="flex gap-2">
                  <div className="flex-1 px-3 py-2 bg-muted/50 border border-input rounded-lg font-mono text-lg text-foreground">
                    {tempPassword}
                  </div>
                  <button
                    onClick={() => copyToClipboard(tempPassword)}
                    className="px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-blue-600 transition-colors flex items-center gap-2"
                  >
                    {copiedPassword ? (
                      <>
                        <Check className="w-4 h-4" />
                        Copiado
                      </>
                    ) : (
                      <>
                        <Copy className="w-4 h-4" />
                        Copiar
                      </>
                    )}
                  </button>
                </div>
              </div>

              <div className="bg-primary/5 border-t-2 border-primary/20 rounded-lg p-3 text-sm text-primary">
                <p className="font-medium mb-1">Instrucciones para el usuario:</p>
                <ol className="list-decimal list-inside space-y-1">
                  <li>Inicia sesión con esta contraseña temporal</li>
                  <li>El sistema te pedirá cambiar la contraseña</li>
                  <li>Elige una contraseña segura y personal</li>
                </ol>
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={() => {
                  setShowTempPasswordModal(false);
                  setTempPassword('');
                  setTempPasswordEmail('');
                  setCopiedPassword(false);
                }}
                className="flex-1 px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-blue-600 transition-colors"
              >
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Assign Country Modal */}
      {showAssignCountryModal && editingUserCountry && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold text-foreground">Asignar País</h3>
              <button
                onClick={() => {
                  setShowAssignCountryModal(false);
                  setEditingUserCountry(null);
                }}
                className="text-muted-foreground hover:text-foreground"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">
                  Usuario
                </label>
                <div className="px-3 py-2 bg-muted/50 border border-input rounded-lg text-foreground">
                  {editingUserCountry.name} ({editingUserCountry.email})
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-1">
                  País Actual
                </label>
                <div className="px-3 py-2 bg-muted/50 border border-input rounded-lg text-foreground">
                  {editingUserCountry.country === 'ARG' ? 'Argentina' : editingUserCountry.country === 'CHL' ? 'Chile' : 'Colombia'}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Nuevo País
                </label>
                <select
                  value={selectedCountry}
                  onChange={(e) => setSelectedCountry(e.target.value as Country)}
                  className="w-full px-3 py-2 border border-input rounded-lg focus:ring-2 focus:ring-ring focus:border-ring"
                >
                  <option value="ARG">Argentina</option>
                  <option value="CHL">Chile</option>
                  <option value="COL">Colombia</option>
                </select>
              </div>

              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 text-sm text-yellow-800">
                <div className="flex items-start gap-2">
                  <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                  <p>Al cambiar el país, el usuario deberá cerrar sesión y volver a iniciar sesión para que los cambios surtan efecto.</p>
                </div>
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={() => {
                  setShowAssignCountryModal(false);
                  setEditingUserCountry(null);
                }}
                className="flex-1 px-4 py-2 border border-input text-foreground rounded-lg hover:bg-muted/50 transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={handleUpdateUserCountry}
                disabled={loading || selectedCountry === editingUserCountry.country}
                className="flex-1 px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-blue-600 transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed"
              >
                Guardar Cambios
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Restaurant Form Modal */}
      {showRestaurantForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold text-foreground">
                {editingRestaurant ? 'Editar Restaurante' : 'Nuevo Restaurante'}
              </h3>
              <button
                onClick={() => {
                  setShowRestaurantForm(false);
                  setEditingRestaurant(null);
                  setRestaurantName('');
                  setRestaurantDescription('');
                  setRestaurantImageUrl('');
                  setRestaurantWhatsapp('');
                  setRestaurantIsDefault(false);
                }}
                className="text-muted-foreground hover:text-foreground"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">
                  Nombre *
                </label>
                <input
                  type="text"
                  value={restaurantName}
                  onChange={(e) => setRestaurantName(e.target.value)}
                  placeholder="Nombre del restaurante"
                  className="w-full px-3 py-2 border border-input rounded-lg focus:outline-none focus:ring-2 focus:ring-ring"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-1">
                  País *
                </label>
                <select
                  value={restaurantCountry}
                  onChange={(e) => setRestaurantCountry(e.target.value as Country)}
                  className="w-full px-3 py-2 border border-input rounded-lg focus:outline-none focus:ring-2 focus:ring-ring"
                >
                  <option value="ARG">Argentina</option>
                  <option value="CHL">Chile</option>
                  <option value="COL">Colombia</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-1">
                  Descripción
                </label>
                <textarea
                  value={restaurantDescription}
                  onChange={(e) => setRestaurantDescription(e.target.value)}
                  placeholder="Descripción del restaurante"
                  rows={3}
                  className="w-full px-3 py-2 border border-input rounded-lg focus:outline-none focus:ring-2 focus:ring-ring"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-1">
                  URL de Imagen
                </label>
                <input
                  type="url"
                  value={restaurantImageUrl}
                  onChange={(e) => setRestaurantImageUrl(e.target.value)}
                  placeholder="https://ejemplo.com/imagen.jpg"
                  className="w-full px-3 py-2 border border-input rounded-lg focus:outline-none focus:ring-2 focus:ring-ring"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-1">
                  Número de WhatsApp
                </label>
                <input
                  type="tel"
                  value={restaurantWhatsapp}
                  onChange={(e) => setRestaurantWhatsapp(e.target.value)}
                  placeholder="+54 9 11 1234 5678"
                  className="w-full px-3 py-2 border border-input rounded-lg focus:outline-none focus:ring-2 focus:ring-ring"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Incluir código de país (ej: +54 para Argentina)
                </p>
              </div>

              <div className="flex items-center gap-2 p-3 bg-primary/5 rounded-lg border border-blue-200">
                <input
                  type="checkbox"
                  id="restaurantIsDefault"
                  checked={restaurantIsDefault}
                  onChange={(e) => setRestaurantIsDefault(e.target.checked)}
                  className="w-4 h-4 text-primary border-input rounded focus:ring-ring"
                />
                <label htmlFor="restaurantIsDefault" className="text-sm font-medium text-foreground cursor-pointer">
                  Visible / Por defecto (solo un restaurante puede estar activo a la vez por país)
                </label>
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={() => {
                  setShowRestaurantForm(false);
                  setEditingRestaurant(null);
                  setRestaurantName('');
                  setRestaurantDescription('');
                  setRestaurantImageUrl('');
                  setRestaurantWhatsapp('');
                  setRestaurantIsDefault(false);
                }}
                disabled={loading}
                className="flex-1 px-4 py-2 border border-input text-foreground rounded-lg hover:bg-muted/50 transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={editingRestaurant ? handleUpdateRestaurant : handleCreateRestaurant}
                disabled={loading || !restaurantName}
                className="flex-1 px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-blue-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    {editingRestaurant ? 'Actualizando...' : 'Creando...'}
                  </>
                ) : (
                  editingRestaurant ? 'Actualizar Restaurante' : 'Crear Restaurante'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Menu Item Form Modal */}
      {showMenuItemForm && selectedRestaurantForMenu && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold text-foreground">
                {editingMenuItem ? 'Editar Plato' : 'Nuevo Plato'}
              </h3>
              <button
                onClick={() => {
                  setShowMenuItemForm(false);
                  setEditingMenuItem(null);
                  setMenuItemName('');
                  setMenuItemDescription('');
                  setMenuItemPrice('');
                  setMenuItemCategory('');
                  setMenuItemImageUrl('');
                  setMenuItemIsCeliac(false);
                }}
                className="text-muted-foreground hover:text-foreground"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">
                  Nombre *
                </label>
                <input
                  type="text"
                  value={menuItemName}
                  onChange={(e) => setMenuItemName(e.target.value)}
                  placeholder="Ej: Milanesa napolitana con fritas"
                  className="w-full px-3 py-2 border border-input rounded-lg focus:outline-none focus:ring-2 focus:ring-ring"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-1">
                  Descripción
                </label>
                <textarea
                  value={menuItemDescription}
                  onChange={(e) => setMenuItemDescription(e.target.value)}
                  placeholder="Descripción del plato"
                  rows={3}
                  className="w-full px-3 py-2 border border-input rounded-lg focus:outline-none focus:ring-2 focus:ring-ring"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-1">
                  Precio
                </label>
                <input
                  type="number"
                  step="0.01"
                  value={menuItemPrice}
                  onChange={(e) => setMenuItemPrice(e.target.value)}
                  placeholder="0.00"
                  className="w-full px-3 py-2 border border-input rounded-lg focus:outline-none focus:ring-2 focus:ring-ring"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-1">
                  Categoría
                </label>
                <input
                  type="text"
                  value={menuItemCategory}
                  onChange={(e) => setMenuItemCategory(e.target.value)}
                  placeholder="Ej: Principales, Ensaladas, Postres"
                  className="w-full px-3 py-2 border border-input rounded-lg focus:outline-none focus:ring-2 focus:ring-ring"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-1">
                  URL de Imagen
                </label>
                <input
                  type="url"
                  value={menuItemImageUrl}
                  onChange={(e) => setMenuItemImageUrl(e.target.value)}
                  placeholder="https://ejemplo.com/imagen.jpg"
                  className="w-full px-3 py-2 border border-input rounded-lg focus:outline-none focus:ring-2 focus:ring-ring"
                />
              </div>

              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="menuItemIsCeliac"
                  checked={menuItemIsCeliac}
                  onChange={(e) => setMenuItemIsCeliac(e.target.checked)}
                  className="w-4 h-4 text-primary border-input rounded focus:ring-ring"
                />
                <label htmlFor="menuItemIsCeliac" className="text-sm font-medium text-foreground">
                  Opción celíaca
                </label>
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={() => {
                  setShowMenuItemForm(false);
                  setEditingMenuItem(null);
                  setMenuItemName('');
                  setMenuItemDescription('');
                  setMenuItemPrice('');
                  setMenuItemCategory('');
                  setMenuItemImageUrl('');
                  setMenuItemIsCeliac(false);
                }}
                disabled={loading}
                className="flex-1 px-4 py-2 border border-input text-foreground rounded-lg hover:bg-muted/50 transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={editingMenuItem ? handleUpdateMenuItem : handleCreateMenuItem}
                disabled={loading || !menuItemName}
                className="flex-1 px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-blue-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    {editingMenuItem ? 'Actualizando...' : 'Creando...'}
                  </>
                ) : (
                  editingMenuItem ? 'Actualizar Plato' : 'Crear Plato'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Manual Close Modal */}
      {showManualCloseModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold text-foreground">Cerrar Almuerzo con Carga Manual</h3>
              <button
                onClick={() => {
                  setShowManualCloseModal(false);
                  resetManualCloseForm();
                }}
                className="text-muted-foreground hover:text-foreground"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-4">
              <div className="bg-primary/5 border-t-2 border-primary/20 rounded-lg p-3 text-sm text-primary">
                <p className="font-medium mb-1">Información:</p>
                <p>Use esta opción para pedidos grupales (ej: pizza) donde no se registran pedidos individuales.</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-1">
                  Nombre del Restaurante *
                </label>
                <input
                  type="text"
                  value={manualRestaurantName}
                  onChange={(e) => setManualRestaurantName(e.target.value)}
                  placeholder="Ej: Pizzeria Don Juan"
                  className="w-full px-3 py-2 border border-input rounded-lg focus:outline-none focus:ring-2 focus:ring-ring"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-1">
                  Cantidad de Comensales *
                </label>
                <input
                  type="number"
                  min="1"
                  value={manualTotalDiners}
                  onChange={(e) => setManualTotalDiners(e.target.value)}
                  placeholder="Ej: 15"
                  className="w-full px-3 py-2 border border-input rounded-lg focus:outline-none focus:ring-2 focus:ring-ring"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-1">
                  Monto Total *
                </label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={manualTotalAmount}
                  onChange={(e) => setManualTotalAmount(e.target.value)}
                  placeholder="Ej: 45000"
                  className="w-full px-3 py-2 border border-input rounded-lg focus:outline-none focus:ring-2 focus:ring-ring"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-1">
                  Notas (opcional)
                </label>
                <textarea
                  value={manualNotes}
                  onChange={(e) => setManualNotes(e.target.value)}
                  placeholder="Ej: 3 pizzas grandes + bebidas"
                  rows={2}
                  className="w-full px-3 py-2 border border-input rounded-lg focus:outline-none focus:ring-2 focus:ring-ring"
                />
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={() => {
                  setShowManualCloseModal(false);
                  resetManualCloseForm();
                }}
                disabled={loading}
                className="flex-1 px-4 py-2 border border-input text-foreground rounded-lg hover:bg-muted/50 transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={handleCloseWithManualEntry}
                disabled={loading || !manualRestaurantName || !manualTotalDiners || !manualTotalAmount}
                className="flex-1 px-4 py-2 bg-warning text-warning-foreground rounded-lg hover:bg-warning/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Procesando...
                  </>
                ) : (
                  'Aplicar y Cerrar'
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
